import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Alert, Button, Card, Input, Modal, Radio, Select, Space, Tag, Typography, message } from "antd";
import type {
    InstallPromotionCommission,
    InstallPromotionCommissionVo,
    Page,
    PlatformBankAccount
} from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { BaseItem as ZhBaseItem, Grid as ZhGrid, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { INSTALL_PROMOTION_COMMISSION_STATE } from "@/meta/expenseManage/installPromotionCommission";
import { WAYBILL_STATE } from "@/meta/logistics/waybill";
import { PLAT_BANK_ACCOUNT_STATE } from "@/meta/expenseManage/platBankAccount";
import { Permission } from "@/views/components/Permission";
import { useUserStore } from "@/stores/react/authStore";

type CommissionInfo = Partial<InstallPromotionCommission>;
type CommissionSummary = Partial<InstallPromotionCommissionVo>;
type UnsettledCommissionRow = Partial<InstallPromotionCommissionVo>;
type UnsettledCommissionPage = Page<UnsettledCommissionRow>;

export interface UnsettledCommissionQuery {
    page: number;
    size: number;
    promoterId: number | string;
    yearMonth: string;
    sortKey?: string;
    sortOrder?: "asc" | "desc";
}

export interface SingleSettleParams {
    isBatch: false;
    id: number | string;
    platformBankAccountId: number;
}

export interface BatchSettleParams {
    isBatch: true;
    yearMonth: string;
    promoterId: number | string;
    platformBankAccountId: number;
}

export interface InstallPromotionCommissionSettlePageProps {
    commissionId?: number | string;
    loadCommission?: (id: number | string) => Promise<CommissionInfo | undefined>;
    loadSummary?: (params: { promoterId: number | string }) => Promise<CommissionSummary | undefined>;
    loadUnsettledAmount?: (params: { promoterId: number | string; yearMonth: string }) => Promise<number>;
    loadUnsettledCommissions?: (params: UnsettledCommissionQuery) => Promise<UnsettledCommissionPage>;
    searchBanks?: (params: { name: string; state: number }) => Promise<PlatformBankAccount[]>;
    settleCommission?: (params: SingleSettleParams | BatchSettleParams) => Promise<unknown>;
}

/** 复用 Vue 单笔结算的佣金详情接口。 */
export async function loadInstallPromotionCommissionForSettle(id: number | string) {
    const response = await request.service.get<CommissionInfo>(`/install-promotion-commission/${id}`);
    return response.data;
}

/** 复用 Vue 批量结算的佣金汇总接口。 */
export async function loadInstallPromotionCommissionSummary(params: { promoterId: number | string }) {
    const response = await request.service.get<CommissionSummary>("/install-promotion-commission/compute", { params });
    return response.data;
}

/** 复用 Vue 批量结算的上月未结算金额接口。 */
export async function loadInstallPromotionUnsettledAmount(params: { promoterId: number | string; yearMonth: string }) {
    const response = await request.service.get<number>("/install-promotion-commission/totalAmount", { params });
    return response.data;
}

/** 复用 Vue 批量结算页的未结算佣金分页接口。 */
export async function loadInstallPromotionUnsettledCommissions(
    params: UnsettledCommissionQuery
): Promise<UnsettledCommissionPage> {
    const response = await request.service.get<UnsettledCommissionPage>("/install-promotion-commission/paging", {
        params
    });
    return response.data;
}

/** 复用 Vue 平台银行账号远程选择接口。 */
export async function searchInstallPromotionSettlementBanks(params: { name: string; state: number }) {
    const response = await request.service.get<PlatformBankAccount[]>("/admin/platform/bank-account/selector", {
        params
    });
    return response.data;
}

/** 复用 Vue 单笔结算接口。 */
export async function settleInstallPromotionCommission(params: SingleSettleParams | BatchSettleParams) {
    const response = await request.service.post("/install-promotion-commission/batch/settle", params);
    return response.data;
}

const formatCustomer = (value?: { institutionName?: string; realName?: string; mobile?: string; code?: string }) =>
    value
        ? `${value.institutionName || value.realName || "--"} | ${value.mobile || "--"} | ${value.code || "--"}`
        : "--";

/** Vue 结算页按上一个自然月请求批量结算金额。 */
export const getPreviousSettlementMonth = (now = new Date()) => {
    const previousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return `${previousMonth.getFullYear()}-${String(previousMonth.getMonth() + 1).padStart(2, "0")}`;
};

/** 费用管理-安装推广佣金单笔结算，保留 Vue 银行账号校验和确认提交。 */
export function InstallPromotionCommissionSettlePage({
    commissionId,
    loadCommission = loadInstallPromotionCommissionForSettle,
    loadSummary = loadInstallPromotionCommissionSummary,
    loadUnsettledAmount = loadInstallPromotionUnsettledAmount,
    loadUnsettledCommissions = loadInstallPromotionUnsettledCommissions,
    searchBanks = searchInstallPromotionSettlementBanks,
    settleCommission = settleInstallPromotionCommission
}: InstallPromotionCommissionSettlePageProps) {
    const params = useParams<{ id: string }>();
    const navigate = useNavigate();
    const banButton = useUserStore(state => state.banButton);
    const resolvedId = commissionId ?? params.id ?? 0;
    const [commission, setCommission] = useState<CommissionInfo>();
    const [commissionSummary, setCommissionSummary] = useState<CommissionSummary>();
    const [isBatchSettle, setIsBatchSettle] = useState(false);
    const [selectAmount, setSelectAmount] = useState(0);
    const [unsettledPage, setUnsettledPage] = useState<UnsettledCommissionPage>({
        list: [],
        pageSize: 10,
        currentPage: 1,
        totalPage: 0,
        total: 0
    });
    const [unsettledPageNumber, setUnsettledPageNumber] = useState(1);
    const [bankSearch, setBankSearch] = useState("");
    const [banks, setBanks] = useState<PlatformBankAccount[]>([]);
    const [bankId, setBankId] = useState<number>();
    const [bankError, setBankError] = useState("");
    const [commissionError, setCommissionError] = useState("");
    const [bankSearchError, setBankSearchError] = useState("");
    const [settleError, setSettleError] = useState("");
    const [confirmVisible, setConfirmVisible] = useState(false);
    const [settling, setSettling] = useState(false);
    const [yearMonth, setYearMonth] = useState(() => getPreviousSettlementMonth());
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">();

    useEffect(() => {
        let disposed = false;
        void loadCommission(resolvedId)
            .then(result => {
                if (!disposed) {
                    setCommission(result);
                    setCommissionError("");
                }
            })
            .catch(() => {
                // 远程详情失败时保留页面结构，并给出可观察的错误反馈，避免 Promise 未处理。
                if (!disposed) setCommissionError("佣金详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadCommission, resolvedId]);

    useEffect(() => {
        if (commission?.promoterId == null) return;
        let disposed = false;
        void loadSummary({ promoterId: commission.promoterId }).then(result => {
            if (!disposed) setCommissionSummary(result);
        });
        return () => {
            disposed = true;
        };
    }, [commission?.promoterId, loadSummary]);

    useEffect(() => {
        if (!isBatchSettle || commission?.promoterId == null) return;
        let disposed = false;
        void loadUnsettledAmount({ promoterId: commission.promoterId, yearMonth }).then(result => {
            if (!disposed) setSelectAmount(result);
        });
        return () => {
            disposed = true;
        };
    }, [commission?.promoterId, isBatchSettle, loadUnsettledAmount, yearMonth]);

    useEffect(() => {
        if (!isBatchSettle || commission?.promoterId == null) return;
        let disposed = false;
        const query: UnsettledCommissionQuery = {
            page: unsettledPageNumber,
            size: 10,
            promoterId: commission.promoterId,
            yearMonth
        };
        if (sortOrder) {
            query.sortKey = "created_time";
            query.sortOrder = sortOrder;
        }
        void loadUnsettledCommissions(query).then(result => {
            if (!disposed) setUnsettledPage(result);
        });
        return () => {
            disposed = true;
        };
    }, [commission?.promoterId, isBatchSettle, loadUnsettledCommissions, sortOrder, unsettledPageNumber, yearMonth]);

    useEffect(() => {
        if (!bankSearch) {
            setBankSearchError("");
            return;
        }
        let disposed = false;
        setBankSearchError("");
        void searchBanks({ name: bankSearch, state: PLAT_BANK_ACCOUNT_STATE.ENABLED.code })
            .then(result => {
                if (!disposed) {
                    setBanks(result);
                    setBankSearchError("");
                }
            })
            .catch(() => {
                // 搜索失败时清空过期选项，避免用户误选旧数据，并保留重试入口。
                if (!disposed) {
                    setBanks([]);
                    setBankSearchError("平台银行账号搜索失败");
                }
            });
        return () => {
            disposed = true;
        };
    }, [bankSearch, searchBanks]);

    const selectedBank = banks.find(bank => Number(bank.id) === bankId);

    // 用 zhihao-ui-react 列契约承接 Vue 批量结算明细的字段、状态和详情链接。
    const unsettledColumns: TableColumnProps<UnsettledCommissionRow>[] = [
        {
            key: "id",
            label: "编号",
            minWidth: 144,
            prop: "commission",
            render: ({ row }) =>
                row.commission?.id == null ? (
                    DEFAULT_VALUE
                ) : (
                    <Link to={`/expenseManagement/installPromotion/detail/${row.commission.id}`}>
                        {row.commission.id}
                    </Link>
                )
        },
        {
            key: "orderShipId",
            label: "船运单",
            minWidth: 218,
            prop: "commission",
            render: ({ row }) =>
                row.commission?.orderShipId == null ? (
                    DEFAULT_VALUE
                ) : (
                    <>
                        {/* 沿用 Vue 船运单详情地址，避免批量结算明细跳转到不存在的旧路径。 */}
                        <Link to={`/logistics/shippingNeed/waybillManagementInfo/${row.commission.orderShipId}`}>
                            {row.commission.orderShipId}
                        </Link>
                        （{WAYBILL_STATE.getIntroByCode(row.orderShipState) || DEFAULT_VALUE}）
                    </>
                )
        },
        {
            key: "commissionAmount",
            label: "结佣金额（￥）",
            minWidth: 130,
            prop: "commission",
            render: ({ row }) => row.commission?.commissionAmount ?? DEFAULT_VALUE
        },
        {
            key: "state",
            label: "状态",
            minWidth: 110,
            prop: "commission",
            render: ({ row }) => (
                <Tag>{INSTALL_PROMOTION_COMMISSION_STATE.getIntroByCode(row.commission?.state) || DEFAULT_VALUE}</Tag>
            )
        },
        {
            key: "createdTime",
            label: "创建时间",
            width: 160,
            prop: "createdTime",
            sortable: true,
            render: ({ row }) => formatDate(row.commission?.createdTime)
        }
    ];

    const submitSettlement = () => {
        if (!bankId) {
            setBankError("请选择平台银行账号");
            return;
        }
        setBankError("");
        setSettleError("");
        setConfirmVisible(true);
    };

    const confirmSettlement = async () => {
        if (!bankId) return;
        setSettling(true);
        setSettleError("");
        try {
            const params: SingleSettleParams | BatchSettleParams = isBatchSettle
                ? {
                      isBatch: true,
                      yearMonth,
                      promoterId: commission?.promoterId ?? resolvedId,
                      platformBankAccountId: bankId
                  }
                : { isBatch: false, id: resolvedId, platformBankAccountId: bankId };
            await settleCommission(params);
            // 保留 Vue 结算成功后的即时反馈，再返回上一页。
            message.success("已结算");
            setConfirmVisible(false);
            navigate(-1);
        } catch {
            // 失败时保留确认弹窗，避免用户误以为已完成结算，并允许重新提交。
            setSettleError("结算失败，请稍后重试");
        } finally {
            setSettling(false);
        }
    };

    return (
        <section aria-label="install-promotion-commission-settle-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <Typography.Title className="mb-4 mt-0" level={4}>
                    安装推广佣金结算
                </Typography.Title>
                {commissionError ? <Alert message={commissionError} role="alert" type="error" /> : null}
                <Space direction="vertical" size="middle" className="w-full">
                    <Space>
                        <Typography.Text>批量结算</Typography.Text>
                        <Radio.Group
                            aria-label="批量结算"
                            value={isBatchSettle ? 1 : 0}
                            onChange={event => setIsBatchSettle(event.target.value === 1)}
                        >
                            <Radio value={1}>是</Radio>
                            <Radio value={0}>否</Radio>
                        </Radio.Group>
                    </Space>
                    <Typography.Text>推介人账号：{formatCustomer(commission?.promoterInfo)}</Typography.Text>
                    {isBatchSettle ? (
                        <>
                            <ZhTable<UnsettledCommissionRow>
                                data={unsettledPage.list || []}
                                columns={unsettledColumns}
                                pagination={false}
                                rowKey={row => String(row.commission?.id || row.virtualId || "commission")}
                                onSortChange={({ prop, order }) => {
                                    if (prop !== "createdTime") return;
                                    setSortOrder(
                                        order === "ascending" ? "asc" : order === "descending" ? "desc" : undefined
                                    );
                                    setUnsettledPageNumber(1);
                                }}
                                isShowResetBtn={false}
                                isShowSearchBtn={false}
                                tableHeader={
                                    <div className="admin-table-composed-header">
                                        <Typography.Text>
                                            总佣金：
                                            {commissionSummary?.totalAmount == null
                                                ? "--"
                                                : `${commissionSummary.totalAmount}元`}
                                        </Typography.Text>

                                        <Typography.Text>
                                            已结算：
                                            {commissionSummary?.settledAmount == null
                                                ? "--"
                                                : `${commissionSummary.settledAmount}元`}
                                        </Typography.Text>

                                        <Typography.Text>
                                            未结算：
                                            {commissionSummary?.notSettledAmount == null
                                                ? "--"
                                                : `${commissionSummary.notSettledAmount}元`}
                                        </Typography.Text>

                                        <Typography.Text>结佣金额：{selectAmount}元</Typography.Text>

                                        <label htmlFor="settlement-year-month">结算月份</label>

                                        <Input
                                            id="settlement-year-month"
                                            aria-label="结算月份"
                                            placeholder="请选择结算月份"
                                            type="month"
                                            value={yearMonth}
                                            max={getPreviousSettlementMonth()}
                                            onChange={event => {
                                                setYearMonth(event.target.value || getPreviousSettlementMonth());
                                                setUnsettledPageNumber(1);
                                            }}
                                        />
                                    </div>
                                }
                                append={
                                    <div className="admin-table-composed-pagination">
                                        <ZhPagination
                                            current={unsettledPageNumber}
                                            pageSize={20}
                                            total={unsettledPage.total || 0}
                                            hideOnSinglePage
                                            onChange={page => setUnsettledPageNumber(page)}
                                        />
                                    </div>
                                }
                            />
                        </>
                    ) : (
                        <Typography.Text>
                            结佣金额：{commission?.commissionAmount == null ? "--" : `${commission.commissionAmount}元`}
                        </Typography.Text>
                    )}
                    <label htmlFor="settlement-bank-search">平台银行账号</label>
                    <Input
                        id="settlement-bank-search"
                        aria-label="平台银行账号搜索"
                        placeholder="请输入平台银行账号搜索"
                        value={bankSearch}
                        onChange={event => setBankSearch(event.target.value)}
                    />
                    <Select
                        aria-label="平台银行账号"
                        placeholder="请选择平台银行账号"
                        value={bankId}
                        options={banks.map(bank => ({ label: bank.name, value: Number(bank.id) }))}
                        onChange={value => {
                            setBankId(value);
                            setBankError("");
                        }}
                    />
                    {selectedBank ? <BankInfoBlock bank={selectedBank} /> : null}
                    {bankSearchError ? <Alert message={bankSearchError} role="alert" type="error" /> : null}
                    {bankError ? <Alert message={bankError} role="alert" type="error" /> : null}
                    <Space>
                        <Permission anyOf={["ROLE_DEVICE_BONUS_W"]}>
                            <Button aria-label="结算" disabled={banButton} type="primary" onClick={submitSettlement}>
                                结算
                            </Button>
                        </Permission>
                        <Button onClick={() => navigate(-1)}>返回</Button>
                    </Space>
                </Space>
            </Card>
            <Modal
                title="是否结算"
                open={confirmVisible}
                okText="确认"
                cancelText="取消"
                confirmLoading={settling}
                onCancel={() => setConfirmVisible(false)}
                onOk={() => void confirmSettlement()}
            >
                {settleError ? <Alert role="alert" type="error" title={settleError} /> : null}
                <Typography.Paragraph>
                    结佣金额：{isBatchSettle ? selectAmount : (commission?.commissionAmount ?? "--")}元
                </Typography.Paragraph>
                <Typography.Paragraph>
                    请确认已转账给推介人{formatCustomer(commission?.promoterInfo)}
                </Typography.Paragraph>
            </Modal>
        </section>
    );
}

function BankInfoBlock({ bank }: { bank: PlatformBankAccount }) {
    return (
        <ZhGrid cols={3} gap={[10, 20]}>
            {/* 保持 Vue 结算页的银行信息字段顺序与空值展示口径。 */}
            <ZhBaseItem label="开户行名称" value={bank.name || DEFAULT_VALUE} />
            <ZhBaseItem label="银行账户" value={bank.account || DEFAULT_VALUE} />
            <ZhBaseItem label="开户行" value={bank.openingBank || DEFAULT_VALUE} />
        </ZhGrid>
    );
}

const formatDate = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);
